import React, { useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Button } from "antd";
import { LogoutOutlined } from "@ant-design/icons";
import firebase from "firebase/compat/app";

// Ancien marqueur d'impersonation (localStorage), remplacé par les claims du jeton
const LEGACY_STORAGE_KEY = "impersonation";

function clearLegacyMarker() {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (e) {
    // Stockage indisponible : rien à nettoyer
  }
}

function formatRemaining(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Bandeau de connexion déléguée : un admin Solidoc agit en tant qu'un utilisateur client
 * (support). Détectée par les claims du jeton (delegation_id, delegation_exp), pas par le
 * navigateur. À l'expiration (60 min) ou sur le bouton, la délégation est close côté serveur
 * (entrée delegation.end au journal) puis la session est fermée.
 */
export default function ImpersonationBanner() {
  const user = useSelector((state) => state.user, shallowEqual);
  const [session, setSession] = useState(null);
  const [now, setNow] = useState(Date.now());
  const endingRef = useRef(false);
  const uid = user ? user.uid : null;

  useEffect(() => {
    clearLegacyMarker();
    const current = firebase.auth().currentUser;
    if (!uid || !current) {
      setSession(null);
      return undefined;
    }
    let cancelled = false;
    current
      .getIdTokenResult()
      .then((result) => {
        if (cancelled) return;
        const claims = result.claims || {};
        setSession(
          claims.delegation_id
            ? {
                delegatedByEmail: claims.delegated_by_email || null,
                expiresAt: (claims.delegation_exp || 0) * 1000,
              }
            : null
        );
      })
      .catch(() => setSession(null));
    return () => {
      cancelled = true;
    };
  }, [uid]);

  useEffect(() => {
    if (!session) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [session]);

  const endSession = useCallback(async (reason) => {
    if (endingRef.current) return;
    endingRef.current = true;
    try {
      await firebase.app().functions("europe-west1").httpsCallable("endDelegatedSession")({
        reason,
      });
    } catch (e) {
      console.error("Clôture de la connexion déléguée impossible", e);
    }
    await firebase.auth().signOut();
    window.location.href = "/login";
  }, []);

  useEffect(() => {
    if (session && now >= session.expiresAt) endSession("expired");
  }, [session, now, endSession]);

  if (!session || !user) {
    return null;
  }

  return (
    <div
      style={{
        background: "#ff4d4f",
        color: "white",
        padding: "8px 16px",
        textAlign: "center",
        fontWeight: 600,
        position: "sticky",
        top: 0,
        zIndex: 9999,
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      }}
    >
      ⚠️ Connexion déléguée — vous agissez en tant que{" "}
      <span style={{ textDecoration: "underline" }}>{user.email}</span>
      {session.delegatedByEmail ? (
        <span style={{ opacity: 0.85, marginLeft: 8 }}>(par {session.delegatedByEmail})</span>
      ) : null}
      <span style={{ opacity: 0.85, marginLeft: 8 }}>
        · fin dans {formatRemaining(session.expiresAt - now)} · actions tracées à votre nom
      </span>
      <Button
        size="small"
        icon={<LogoutOutlined />}
        onClick={() => endSession("user")}
        style={{ marginLeft: 16 }}
      >
        Terminer la connexion déléguée
      </Button>
    </div>
  );
}
